import React, { useState, useCallback, useRef, useEffect } from 'react';

import useClassy from '@core/hooks/useClassy';
import classy from '@core/utils/classy';

import Button from '@ui/Button';
import Icon from '@ui/Icon';
import Spinner from '@ui/Spinner';

import classes from './ChatInput.module.scss';

interface ChatInputProps {
  isLoading?: boolean;
  onSendMessage: (message: string) => void;
  variant?: 'bottom' | 'empty';
}

export default function ChatInput({ onSendMessage, isLoading, variant = 'bottom' }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bem = useClassy(classes, 'ChatInput');

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (message.trim() && !isLoading) {
        onSendMessage(message.trim());
        setMessage('');
      }
    },
    [message, isLoading, onSendMessage],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  }, []);

  // Focus the textarea when component mounts
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Refocus the textarea after message is sent
  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading]);

  return (
    <div className={classy(bem('&'), 'rm-AskAi-input')}>
      <form className={bem('-form')} onSubmit={handleSubmit}>
        <div className={variant === 'empty' ? bem('-container', '-container_empty') : bem('-container')}>
          <textarea
            ref={textareaRef}
            className={variant === 'empty' ? bem('-textarea', '-textarea_empty') : bem('-textarea')}
            contentEditable={false}
            disabled={isLoading}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyPress}
            placeholder="Ask a question"
            rows={1}
            value={message}
          />
          <Button
            className={bem('-button')}
            disabled={!message.trim() || isLoading}
            kind="secondary"
            outline
            size="sm"
            type="submit"
          >
            {isLoading ? <Spinner size="sm" /> : <Icon aria-hidden="true" name="arrow-up" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
